/**
 * The `lg`-and-up layout of `../TextImageSlide.tsx`. The section title is an `h1` with a divider
 * above the first slide. Slides alternate: even-indexed ones have the text on the left and the
 * photo on the right on the primary background; odd-indexed ones swap sides on the secondary
 * background. In the text column, `benefitsSection` and `capabilitiesSection` sit side by side,
 * with the call-to-action link below them. The mobile counterpart is
 * `../Mobile/TextImageSlideMobile.tsx`.
 */
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection } from "@ryerson/frontend.layout";
import { Grid, Column, Row } from "@ryerson/frontend.layout";
import { TextImageSlideProps, Section, Bullet } from "../TextImageSlide";

const Divider = styled.hr`
	width: 100%;
	opacity: 20%;
	margin-top: 59px;
	margin-bottom: 55px;
	${(props: any) => {
		const { theme } = props;
		return css`
			color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

const TextContainer = styled.div`
	display: block;
	width: 85%;
	padding-bottom: 40px;
	height: auto;
	${(props: TextColumnProps) => {
		return css`
			margin: ${props.paddingDirection === "left" ? "0 auto 0 0" : " 0 0 0 auto"};
		`;
	}}
`;

type TextColumnProps = {
	section: Section;
	/** `"left"` pushes the 85%-wide text block to the left edge; anything else to the right. */
	paddingDirection: string;
};

/** Desktop equipment and process slides; see the file header. */
const TextImageSlideDesktop: React.FC<TextImageSlideProps> = (props) => {
	const { theme } = useTheme();

	// Declared inside the component, so every render creates a new component type and React
	// remounts each slide's text column.
	const TextColumn = (props: TextColumnProps) => {
		const section = props.section;

		return (
			<TextContainer {...props}>
				<Typography variant="h2">{section.title}</Typography>
				<Typography
					variant="p"
					size="lg"
					css={css`
						margin-top: 32px;
						margin-bottom: 24px;
					`}
				>
					{section.description}
				</Typography>
				{section.description2 ? (
					<>
						<Typography
							variant="p"
							size="lg"
							css={css`
								margin-top: 32px;
								margin-bottom: 24px;
							`}
						>
							{section.description2}
						</Typography>
					</>
				) : (
					<></>
				)}
				{section.descriptionArray &&
					section.descriptionArray.map((description: string, idx: number) => (
						<React.Fragment key={idx}>
							<Typography
								variant="p"
								size="lg"
								css={css`
									margin-top: 32px;
									margin-bottom: 24px;
									white-space: pre-wrap;
								`}
							>
								{description}
							</Typography>
						</React.Fragment>
					))}
				<Grid>
					<Row>
						<Column lg={6}>
							{section.benefitsSection ? (
								<>
									<Typography variant="h3">
										{section.benefitsSection.title.length > 0
											? section.benefitsSection.title
											: "\u00A0"}
									</Typography>

									<ul
										style={{
											paddingLeft: "17.5px",
										}}
									>
										{section.benefitsSection.bullet.map(
											(bullet: Bullet, idx: number) => (
												<li key={idx}>
													{bullet.bold ? (
														<>
															<Typography
																variant="span"
																weight="bold"
																size="md"
															>
																{bullet.bold}
															</Typography>
															<Typography variant="span" size="md">
																{bullet.normal}
															</Typography>
														</>
													) : (
														<Typography variant="span" size="md">
															{bullet.normal}
														</Typography>
													)}
												</li>
											)
										)}
									</ul>
								</>
							) : (
								<></>
							)}
						</Column>
						<Column lg={6}>
							{section.capabilitiesSection ? (
								<>
									<Typography variant="h3">
										{section.capabilitiesSection.title.length > 0
											? section.capabilitiesSection.title
											: "\u00A0"}
									</Typography>

									<ul
										style={{
											paddingLeft: "17.5px",
										}}
									>
										{section.capabilitiesSection.bullet.map(
											(bullet: Bullet, idx: number) => (
												<li key={idx}>
													{bullet.bold ? (
														<>
															<Typography
																variant="span"
																weight="bold"
																size="md"
															>
																{bullet.bold}
															</Typography>
															<Typography variant="span" size="md">
																{bullet.normal}
															</Typography>
														</>
													) : (
														<Typography variant="span" size="md">
															{bullet.normal}
														</Typography>
													)}
												</li>
											)
										)}
									</ul>
								</>
							) : (
								<></>
							)}
						</Column>
					</Row>
					<Row>
						<Column lg={6}>
							{/* The hash goes in separately: `/contact-us#quoteRequest` becomes
							    `to="/contact-us"` with `hash="quoteRequest"`. */}
							<ButtonLink
								next={true}
								to={section.actionLink?.split("#")[0] ?? "#"}
								{...(section.actionLink
									? { hash: section.actionLink.split("#")[1] }
									: {})}
								type="primary"
								orientation="left"
								linkType="internal"
								label={section.actionText}
							/>
						</Column>
						<Column lg={6}>
							<></>
						</Column>
					</Row>
				</Grid>
			</TextContainer>
		);
	};

	return (
		<>
			{props.sections.map((section: Section, index: number) => (
				<React.Fragment key={section.title}>
					<ContentSection
						vPadding="104px"
						type={index % 2 === 0 ? "primary" : "secondary"}
					>
						{index === 0 ? (
							<>
								<Typography variant="h1">{props.title}</Typography>
								<Divider theme={theme} />
							</>
						) : (
							<></>
						)}
						<Grid>
							{index % 2 === 0 ? (
								<Row>
									<Column lg={6}>
										<TextColumn section={section} paddingDirection="left" />
									</Column>
									<Column lg={6}>
										<img
											css={{ width: "100%" }}
											src={section.imageUrl}
											alt={section.title}
										></img>
									</Column>
								</Row>
							) : (
								<Row>
									<Column lg={6}>
										<img
											css={{ width: "100%" }}
											src={section.imageUrl}
											alt={section.title}
										></img>
									</Column>
									<Column lg={6}>
										<TextColumn section={section} paddingDirection="right" />
									</Column>
								</Row>
							)}
						</Grid>
					</ContentSection>
				</React.Fragment>
			))}
		</>
	);
};

export default TextImageSlideDesktop;
